import React, { useState } from 'react';
import { AIAssistantConfig } from '../types/profile';
import { useLocalization } from '../engine/localization/context';
import { Bot, X, Sparkles, AlertCircle } from 'lucide-react';

interface AiAssistantTriggerProps {
  config?: AIAssistantConfig;
}

export const AiAssistantTrigger: React.FC<AiAssistantTriggerProps> = ({ config }) => {
  const { resolve, t } = useLocalization();
  const [isOpen, setIsOpen] = useState(false);

  if (!config || !config.enabled) return null;

  const botName = resolve(config.botName);
  const welcomeMessage = resolve(config.welcomeMessage);
  const isDisabled = config.providerMode === 'AI_DISABLED';

  return (
    <>
      {/* Floating trigger button */}
      <div className="fixed bottom-5 end-5 z-40">
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          aria-label={botName}
          className="flex items-center gap-2 px-4 py-3 rounded-full bg-gradient-to-r from-purple-600 via-indigo-600 to-sky-600 text-white font-bold text-xs shadow-[0_4px_25px_rgba(139,92,246,0.4)] hover:shadow-[0_6px_30px_rgba(139,92,246,0.6)] transition-all duration-200 hover:scale-105 active:scale-95 group"
        >
          <Bot className="w-5 h-5 animate-pulse" />
          <span className="hidden sm:inline">{botName}</span>
          <Sparkles className="w-3.5 h-3.5 text-amber-300" />
        </button>
      </div>

      {/* Assistant Modal / Drawer */}
      {isOpen && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/70 backdrop-blur-sm animate-fade-in"
          onClick={() => setIsOpen(false)}
        >
          <div
            className="relative w-full max-w-sm rounded-t-3xl sm:rounded-3xl bg-slate-900 border border-purple-500/30 p-5 shadow-2xl text-white flex flex-col max-h-[80vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-800 pb-3 mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-purple-500/20 border border-purple-500/40 flex items-center justify-center text-purple-400">
                  <Bot className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-white">{botName}</h4>
                  <span className="text-[10px] text-slate-400 font-mono">
                    Mode: {config.providerMode}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsOpen(false)}
                aria-label={t('action.close')}
                className="p-1.5 rounded-full bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto space-y-3 py-2 text-xs">
              <div className="p-3 rounded-2xl bg-purple-950/40 border border-purple-800/40 text-purple-200 leading-relaxed">
                <p>{welcomeMessage}</p>
              </div>

              {isDisabled && (
                <div className="flex items-start gap-2 p-3 rounded-2xl bg-slate-800/70 border border-slate-700 text-slate-300">
                  <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-[11px] leading-relaxed">
                    {t('action.aiDisabled')}
                  </p>
                </div>
              )}
            </div>

            {/* Input area (disabled state notice) */}
            <div className="pt-3 border-t border-slate-800">
              <div className="relative">
                <input
                  type="text"
                  disabled={isDisabled}
                  placeholder={resolve(config.placeholder)}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-800 border border-slate-700 text-xs text-white placeholder-slate-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

